import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { TimesheetPageShiftFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { useAsyncCan } from 'modules/core/lib/useAsyncCan'

import BulkChange from 'modules/attendance/components/ClockIn/Shifts/DailyShifts/DailyTable/BulkChange'
import {
  Cycle,
  useAttendanceDateNavigator,
} from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { TimeSettingsCategoryProvider } from 'modules/attendance/lib/timeSettingsCategoryContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'

import BankOfHoursButton from './BankOfHoursButton'
import TimeTrackingDatePicker from './TimeTrackingDatePicker'

interface Props {
  employeeId: number
  showHourBalance: boolean
  isDaily: boolean
  submitBulkDailyShiftChange?: (shifts: TimesheetPageShiftFragment[]) => void
}

const TimesheetButtons = ({
  employeeId,
  showHourBalance,
  isDaily,
  submitBulkDailyShiftChange,
}: React.PropsWithChildren<Props>) => {
  const { pathParams, dateFrom: from, dateTo: to } = useAttendanceDateNavigator()
  const currentMonth = moment(from).startOf('month')
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { employee } = useCurrentSession()
  const timeSheetData = useTimesheetData()
  const { value: canCreate } = useAsyncCan('attendance.create_overtime_request', {
    id: employeeId,
    policyTargetClass: '::Employee',
  })

  const { employeesSettings, featureByName, shifts, attendancePeriods } = timeSheetData
  const rangeDays = AttendanceShiftUtils.daysBetweenDates(moment(from), moment(to))

  const halfDayShiftsHash = AttendanceShiftUtils.halfDayShifts({
    attendancePeriods: attendancePeriods,
    employeeId,
    shifts,
    rangeDays,
  })

  const hasBankOfHours = TimesheetFeatures.BANK_OF_HOURS in featureByName
  const userCheckingOwnTimesheet = employeeId === employee?.id

  const params = {
    ...pathParams,
    employeeId,
  }

  const location = useLocation()
  const isEmployeePath = location.pathname.includes(navigation.clockIn.path)
  const employeeUrl = navigation.clockIn.periodTypeYearMonthDay
  const adminUrl = navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId
  let compensationBreakdownUrl, bankOfHoursUrl, exportUrl, requestOvertimeUrl

  if (isEmployeePath) {
    exportUrl = employeeUrl.exportsEmployeeId.fullPath(params)
    requestOvertimeUrl = employeeUrl.requestOvertimeEmployeeId.fullPath(params)
    compensationBreakdownUrl = employeeUrl.compensationBreakdownEmployeeId.fullPath(params)
    bankOfHoursUrl = employeeUrl.bankOfHoursEmployeeId.fullPath(params)
  } else {
    exportUrl = adminUrl.exports.fullPath(params)
    requestOvertimeUrl = adminUrl.requestOvertime.fullPath(params)
    compensationBreakdownUrl = adminUrl.compensationBreakdownEmployeeId.fullPath(params)
    bankOfHoursUrl = adminUrl.bankOfHoursEmployeeId.fullPath(params)
  }

  const onDateChange = (dateFrom: Date, _dateTo: Date, cycle: Cycle) => {
    const data = {
      periodType: cycle,
      year: dateFrom.getFullYear(),
      month: dateFrom.getMonth() + 1,
      day: dateFrom.getDate(),
      employeeId,
    }

    if (isEmployeePath) {
      navigate(employeeUrl.fullPath(data))
    } else {
      navigate(adminUrl.fullPath(data))
    }
  }

  const payrollCompensationEnabled =
    EmployeesSettingsUtils.hasPayrollCompensationEnabled(employeesSettings)

  const timeoffCompensationEnabled = employeesSettings.some(
    (setting) => setting.timeoffCompensation
  )

  const showOverTimeRequestButton =
    EmployeesSettingsUtils.hasOvertimeAuthorization(employeesSettings) && canCreate

  const showCompensateButton =
    hasBankOfHours &&
    showHourBalance &&
    (timeoffCompensationEnabled || payrollCompensationEnabled) &&
    !isDaily

  const showBankOfHoursButton = hasBankOfHours && showHourBalance && !isDaily

  const showBulkChangeDailyShiftButton =
    !!attendancePeriods?.length && isDaily && !!submitBulkDailyShiftChange

  const onClickBankOfHours = () => {
    track(trackingEvents.timeTracking.openBankOfHours, {
      user_checking_own_timesheet: userCheckingOwnTimesheet,
    })
    navigate(bankOfHoursUrl)
  }

  return (
    <TimeSettingsCategoryProvider>
      <Box flexDirection="row" justifyContent="spaceBetween" paddingBottom="s24">
        {showBankOfHoursButton ? (
          <BankOfHoursButton onClick={() => onClickBankOfHours()} />
        ) : (
          <Box width="s2" />
        )}
        <Box flexDirection="row" gap="s12" alignSelf="flexEnd">
          <TimeTrackingDatePicker
            regularPeriods={['week', 'fortnight', 'month']}
            onChange={onDateChange}
          />

          {showBulkChangeDailyShiftButton && (
            <BulkChange
              shifts={Object.values(halfDayShiftsHash).flat() ?? []}
              currentMonth={currentMonth}
              submitBulkChange={submitBulkDailyShiftChange}
            />
          )}
          {showCompensateButton && (
            <Button
              type="secondary"
              iconName="replace"
              onClick={() => navigate(compensationBreakdownUrl)}
            >
              {i18n.t('clock_in.header.compensation')}
            </Button>
          )}
          {showOverTimeRequestButton && (
            <Button type="secondary" iconName="add" onClick={() => navigate(requestOvertimeUrl)}>
              {i18n.t('clock_in.header.requestOvertime.title')}
            </Button>
          )}
          <Button
            type="secondary"
            iconName="download"
            title={i18n.t('clock_in.header.download.time_sheet')}
            onClick={() => navigate(exportUrl)}
          />
        </Box>
      </Box>
    </TimeSettingsCategoryProvider>
  )
}

export default TimesheetButtons
